import { useState, useEffect, useRef } from "react";
import { User, Lightbulb, Globe, BookOpen, Users, Shield, Clock, ChevronDown, Linkedin, Search, Megaphone } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ── Scroll-triggered fade-in hook ── */
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/* ── Framework dimension colors (explicit for Tailwind) ── */
const DIMENSION_STYLES = {
  dignity:      { bg: "bg-emerald-500/15", text: "text-emerald-400", border: "hover:border-emerald-500/30" },
  collective:   { bg: "bg-cyan-500/15",    text: "text-cyan-400",    border: "hover:border-cyan-500/30" },
  ethical:      { bg: "bg-violet-500/15",   text: "text-violet-400",  border: "hover:border-violet-500/30" },
  longterm:     { bg: "bg-amber-500/15",    text: "text-amber-400",   border: "hover:border-amber-500/30" },
};

const PILLAR_STYLES = {
  mapping:    { bg: "bg-emerald-500/15", text: "text-emerald-400" },
  research:   { bg: "bg-cyan-500/15",    text: "text-cyan-400" },
  amplifying: { bg: "bg-violet-500/15",   text: "text-violet-400" },
};

/* ── Accordion Item ── */
const FrameworkDimension = ({ icon: Icon, title, styleKey, children, index }) => {
  const [open, setOpen] = useState(false);
  const s = DIMENSION_STYLES[styleKey];
  return (
    <Reveal delay={index * 120}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full text-left bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-xl p-5 transition-all ${s.border} group`}
        data-testid={`framework-${title.toLowerCase().replace(/\s/g, "-")}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${s.bg}`}>
              <Icon className={`w-5 h-5 ${s.text}`} />
            </div>
            <h3 className="font-semibold text-base">{title}</h3>
          </div>
          <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </div>
        <div className={`overflow-hidden transition-all duration-500 ${open ? "max-h-60 opacity-100 mt-4" : "max-h-0 opacity-0"}`}>
          <div className="text-sm leading-relaxed text-muted-foreground pl-[52px]">
            {children}
          </div>
        </div>
      </button>
    </Reveal>
  );
};

/* ── Main Page ── */
export const AboutPage = () => {
  const [pulse, setPulse] = useState(null);
  useEffect(() => { axios.get(`${API}/pulse`).then(r => setPulse(r.data)).catch(() => {}); }, []);
  const totalEntities = pulse?.total_entities?.toLocaleString() || "100,000+";
  const totalApps = pulse?.total_apps?.toLocaleString() || "67,000+";
  const totalDevices = pulse?.total_devices?.toLocaleString() || "2,300+";
  const countriesCount = pulse?.countries_count || 200;

  return (
    <div className="max-w-3xl mx-auto pb-12" data-testid="about-page">

      {/* ─── SECTION 1: Hero ─── */}
      <section className="relative py-20 text-center overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/[0.07] via-transparent to-transparent pointer-events-none" />
        <div className="relative space-y-5">
          <Reveal>
            <p className="text-emerald-400 text-xs font-semibold tracking-[0.25em] uppercase">Amplifying Conscious Innovation</p>
          </Reveal>
          <Reveal delay={150}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.15]">
              Innovation with consciousness.<br />
              <span className="text-emerald-400">Technology with humanity.</span>
            </h1>
          </Reveal>
          <Reveal delay={300}>
            <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
              An initiative dedicated to identifying, studying, and amplifying ideas, technologies, and systems that expand human dignity, collective intelligence, and long-term societal well-being.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── SECTION 2: The Why ─── */}
      <section className="pb-12 space-y-5" data-testid="about-aci">
        <Reveal>
          <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
            <p>
              As artificial intelligence and emerging technologies reshape the world at unprecedented speed, innovation must do more than scale efficiency or economic growth. <span className="text-white/90 font-medium">It must strengthen humanity.</span>
            </p>
            <p>
              Amplifying Conscious Innovation explores how builders, researchers, and institutions can consciously design technologies and systems that uplift the human condition.
            </p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <p className="text-emerald-400/80 font-medium italic text-sm border-l-2 border-emerald-500/40 pl-4 py-1">
            Deweaponizing progress is humanity's biggest challenge.
          </p>
        </Reveal>
      </section>

      {/* ─── What We Do — 3 cards ─── */}
      <section className="pb-14">
        <Reveal>
          <h2 className="text-lg font-bold mb-5">What We Do</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: "mapping", icon: Search, title: "Mapping Innovation", desc: "Identifying emerging technologies, research, and initiatives shaping the future of human-centered innovation." },
            { key: "research", icon: BookOpen, title: "Research & Insight", desc: "Publishing analysis and thought leadership on the relationship between technology, society, and human values." },
            { key: "amplifying", icon: Megaphone, title: "Amplifying Builders", desc: "Highlighting the founders, researchers, and institutions creating systems designed to strengthen humanity." },
          ].map((card, i) => {
            const s = PILLAR_STYLES[card.key];
            return (
              <Reveal key={card.key} delay={i * 120}>
                <div className="bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-xl p-5 h-full hover:border-white/[0.12] transition-all hover:-translate-y-0.5 duration-300">
                  <div className={`p-2.5 rounded-lg ${s.bg} w-fit mb-4`}>
                    <card.icon className={`w-5 h-5 ${s.text}`} />
                  </div>
                  <h3 className="font-semibold text-sm mb-2">{card.title}</h3>
                  <p className="text-xs leading-relaxed text-muted-foreground">{card.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ─── SECTION 3: Our Vision ─── */}
      <Reveal>
        <section className="pb-14" data-testid="about-vision">
          <div className="border-l-2 border-emerald-500/50 pl-6 py-1 space-y-3">
            <h2 className="text-lg font-bold text-emerald-300">Our Vision</h2>
            <p className="text-base leading-relaxed text-slate-300">
              We envision a future where innovation strengthens humanity rather than undermines it.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              A future where technologies expand human potential, deepen collective intelligence, and support more equitable, resilient societies. Innovation has the power to reshape the world. The question is whether it will be guided only by capability, or by values.
            </p>
            <p className="text-sm text-muted-foreground/70 italic">
              Conscious innovation ensures that the systems we build contribute to a future where humanity flourishes.
            </p>
          </div>
        </section>
      </Reveal>

      {/* ─── SECTION 4: What is Conscious Innovation ─── */}
      <section className="pb-14 space-y-6" data-testid="about-framework">
        <Reveal>
          <h2 className="text-xl font-bold mb-1">What is Conscious Innovation?</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            The intentional design and development of technologies, systems, and institutions that expand human dignity, collective intelligence, and long-term societal well-being. By embedding ethical awareness, human values, and long-term thinking into innovation, it ensures technological progress aligns with the flourishing of humanity.
          </p>
        </Reveal>

        {/* 4 Dimensions */}
        <div className="space-y-3">
          <FrameworkDimension icon={User} title="Human Dignity" styleKey="dignity" index={0}>
            Innovation should enhance the agency, autonomy, and well-being of individuals. Does this system empower people? Does it reduce harm or inequality?
          </FrameworkDimension>
          <FrameworkDimension icon={Users} title="Collective Intelligence" styleKey="collective" index={1}>
            Innovation should strengthen humanity's ability to learn, collaborate, and solve complex problems together: through knowledge systems, collaborative AI, and open research ecosystems.
          </FrameworkDimension>
          <FrameworkDimension icon={Shield} title="Ethical Design" styleKey="ethical" index={2}>
            Innovation must embed values such as fairness, accountability, and transparency directly into technological systems. Not as an afterthought, but as a design principle.
          </FrameworkDimension>
          <FrameworkDimension icon={Clock} title="Long-Term Impact" styleKey="longterm" index={3}>
            Conscious innovation considers the future consequences of technological development. What systems does this create? Who benefits? What unintended effects may emerge?
          </FrameworkDimension>
        </div>

        {/* Core Idea — Contrast */}
        <Reveal delay={100}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 rounded-xl overflow-hidden border border-white/[0.06]" data-testid="core-idea-contrast">
            <div className="bg-white/[0.02] p-6 border-b md:border-b-0 md:border-r border-white/[0.06]">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/60 mb-3">Traditional Innovation</p>
              <p className="text-lg font-semibold text-slate-400">"How do we scale?"</p>
            </div>
            <div className="bg-emerald-500/[0.06] p-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400/60 mb-3">Conscious Innovation</p>
              <p className="text-lg font-semibold text-emerald-300">"What kind of world does this create?"</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─── SECTION 5: Why This Matters Now ─── */}
      <section className="pb-14 space-y-5" data-testid="about-why-now">
        <Reveal>
          <h2 className="text-xl font-bold">Why This Matters Now</h2>
          <p className="text-sm text-muted-foreground mt-1">Humanity is entering an era shaped by:</p>
        </Reveal>
        <div className="flex flex-wrap gap-2.5">
          {[
            "Artificial Intelligence",
            "Automation",
            "Global Digital Infrastructure",
            "Biotechnology",
            "Planetary-Scale Challenges",
          ].map((item, i) => (
            <Reveal key={item} delay={i * 80}>
              <span className="inline-block bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-2 text-xs font-medium text-slate-300">
                {item}
              </span>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <div className="space-y-3">
            <p className="text-sm leading-relaxed text-muted-foreground">
              Innovation is becoming <span className="text-white/90 font-medium">civilizational infrastructure</span>. The values embedded in these systems will influence society for generations. Without conscious direction, innovation can unintentionally amplify inequality, social fragmentation, and extractive economic models.
            </p>
            <p className="text-sm text-emerald-400/80 font-medium italic">
              Conscious innovation aims to redirect technological progress toward human flourishing and societal resilience.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ─── SECTION 6: The Manifesto ─── */}
      <Reveal>
        <section className="py-10 my-2 rounded-2xl bg-white/[0.02] border border-white/[0.06] px-8 md:px-10" data-testid="about-manifesto">
          <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-400/50 font-semibold mb-8">The Manifesto</p>
          <div className="space-y-5">
            <p className="text-lg leading-relaxed text-slate-200 font-light">Humanity is entering an era of unprecedented technological power.</p>
            <p className="text-sm leading-relaxed text-slate-300">Artificial intelligence, digital systems, and emerging technologies are rapidly transforming how societies function.</p>
            <p className="text-sm leading-relaxed text-muted-foreground">But technological capability alone does not determine the future. <span className="text-white/90 font-medium">Values do.</span></p>
            <p className="text-sm leading-relaxed text-muted-foreground">Innovation is never neutral. Every system reflects the priorities of those who design it.</p>
            <p className="text-sm leading-relaxed text-muted-foreground">Today, much innovation is optimized for speed, scale, and profit, while the deeper dimensions of human flourishing remain underrepresented.</p>
            <p className="text-base leading-relaxed text-slate-200 font-light pt-2">A different path is possible.</p>
            <p className="text-sm leading-relaxed text-muted-foreground">Innovation can strengthen human dignity, expand empathy, and enhance collective intelligence. Technology can be designed to align with humanity rather than distance itself from it.</p>
            <p className="text-sm leading-relaxed text-muted-foreground">Amplifying Conscious Innovation exists to highlight and elevate the people, ideas, and systems working toward that future.</p>
            <div className="pt-6 mt-4 border-t border-white/[0.06] space-y-3">
              <p className="text-sm text-slate-300">Because the defining question of our time is not simply <em>what we can build</em>.</p>
              <p className="text-xl font-semibold text-emerald-400 leading-snug">The deeper question is what we should build.</p>
              <p className="text-sm text-muted-foreground/70">And the answer will shape the future of humanity.</p>
            </div>
          </div>
        </section>
      </Reveal>

      {/* ─── SECTION 7: About the Observatory ─── */}
      <section className="py-10" data-testid="about-observatory">
        <Reveal>
          <div className="bg-gradient-to-r from-emerald-500/[0.06] via-teal-500/[0.06] to-cyan-500/[0.06] border border-emerald-500/20 rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-lg bg-emerald-500/15">
                <Globe className="w-5 h-5 text-emerald-400" />
              </div>
              <h2 className="text-lg font-bold text-emerald-300">About the Observatory</h2>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              The AI Orientation Observatory (AIOO) is a multi-galaxy research initiative tracking how humanity is building and deploying artificial intelligence. Analyzing over {totalEntities} AI entities — {totalApps} software applications and {totalDevices} physical devices, plus companies and policy frameworks — across {countriesCount} countries, the Observatory examines patterns in product orientation, founder demographics, regional trends, governance models, and the convergence of software intelligence with physical hardware.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground mt-3">
              The Observatory includes a <span className="text-amber-400 font-medium">Possibility Engine</span> that maps how existing AI software and hardware can combine to create new solutions, and predicts convergence timelines. This project is part of the broader <span className="text-emerald-400 font-medium">Amplifying Conscious Innovation</span> initiative.
            </p>
          </div>
        </Reveal>
      </section>

      {/* ─── SECTION 8: Methodology & Foundational References ─── */}
      <section className="py-6" data-testid="about-methodology">
        <Reveal>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2.5 rounded-lg bg-violet-500/15">
                <Shield className="w-5 h-5 text-violet-400" />
              </div>
              <h2 className="text-lg font-bold">Methodology & Foundational References</h2>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Classification Framework</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Every AI application is classified into one of four value orientations: <span className="text-emerald-400">Care</span> (human wellbeing), <span className="text-blue-400">Utility</span> (productivity and efficiency), <span className="text-violet-400">Creative</span> (artistic expression), or <span className="text-red-400">Extraction</span> (value extraction). Classification uses multi-model LLM analysis (GPT-4o-mini, Claude Sonnet, Gemini Flash) with 78% consensus match rate and 84% inter-rater agreement.
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">The Conscious Innovation Index (AICCI)</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                  A composite 0-100 score measuring how consciously humanity is building AI. Seven dimensions, each benchmarked against established international standards:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { dim: "Care Orientation", ref: "UN Sustainable Development Goal 3", desc: "Good Health and Well-Being. Benchmark: 25% of AI should serve human wellbeing." },
                    { dim: "Founder Diversity", ref: "UN Women / World Economic Forum", desc: "Gender Parity Index. Benchmark: 50% non-male-only founding teams." },
                    { dim: "Ethical Governance", ref: "OECD AI Principles (2019)", desc: "AI should be transparent, explainable, and accountable. Benchmark: 75%." },
                    { dim: "Geographic Spread", ref: "UNCTAD Technology & Innovation Report", desc: "Technology concentration index. Benchmark: Gini coefficient < 0.30." },
                    { dim: "Extraction Resistance", ref: "EU AI Act (2024)", desc: "High-risk and prohibited AI practices. Benchmark: < 2% extraction-oriented." },
                    { dim: "Inclusion", ref: "WHO / UNDP Inclusion Frameworks", desc: "Underrepresented communities served by tech. Benchmark: 15% inclusive." },
                    { dim: "Human Agency", ref: "IEEE Ethically Aligned Design (EAD)", desc: "User autonomy as a fundamental right. Benchmark: 40% with user control." },
                  ].map((item, i) => (
                    <div key={i} className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-3">
                      <p className="text-xs font-semibold text-slate-300">{item.dim}</p>
                      <p className="text-[10px] text-blue-400 font-medium mt-0.5">{item.ref}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Data Sources</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Product Hunt, HuggingFace Spaces & Models, Apple App Store, Google Play Store (19 languages, 258+ search terms), Futurepedia, There's an AI for That (TAAFT), OECD AI Catalogue, OECD Government AI Reports, AI for Good/ITU, and crowdsourced submissions across 200+ countries. Time period: November 2022 to present (post-ChatGPT era).
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Limitations</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  English-language bias in data sources (mitigated by multi-region scraping). 23% of apps have unknown founder composition (primarily app store listings). LLM classification has ~16% ambiguous cases where orientation is debatable. Country attribution follows app store region or company HQ, not founder nationality. Physical AI device data includes LLM-generated entries that require ongoing validation.
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Physical AI Observatory</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {totalDevices} physical AI devices catalogued worldwide, spanning wearables, robotics, hardware, and embedded systems. Sources include FDA AI/ML Cleared Device Database (1,451 cleared devices as of 2025), CES Innovation Awards, university research labs (Stanford, MIT, Harvard, Oxford, IIT, KAIST, ETH Zurich, CMU, and others), emerging market innovations, and industry databases.
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Possibility Engine & Convergence Framework</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  The Possibility Engine maps how existing software AI and physical AI hardware can combine to create new solutions. Convergence timelines are estimated using Software Maturity (0-100), Hardware Readiness (0-100), and a Convergence Score. This framework draws on industry convergence research from Stanford HAI AI Index, IBM AI Predictions, PwC AI Forecasts, and the Carlota Perez technological revolution model.
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-300 mb-2">Foundational References & Precedents</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                  The following frameworks, standards, and research inspired and inform our methodology. The Observatory, AICCI, and Possibility Engine are independently built instruments; these references represent the intellectual foundations we stand on.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { ref: "Stanford HAI", desc: "AI Index Annual Report. Predictions for AI in 2026." },
                    { ref: "IBM Research", desc: "AI Tech Trends and Predictions 2026." },
                    { ref: "PwC", desc: "AI Predictions: Enterprise AI Forecasts." },
                    { ref: "OECD AI Observatory", desc: "AI Policy Observatory Reports (2023-2026)." },
                    { ref: "UNESCO", desc: "Recommendation on the Ethics of AI (2021)." },
                    { ref: "IEEE", desc: "Ethically Aligned Design (EAD) for Autonomous Systems." },
                    { ref: "EU AI Act", desc: "Regulation 2024/1689 on Harmonised Rules on AI." },
                    { ref: "World Economic Forum", desc: "Global AI Governance Report (2023)." },
                    { ref: "World Health Organization", desc: "Ethics and Governance of AI for Health." },
                    { ref: "UNCTAD", desc: "Technology and Innovation Report: Catching Technological Waves." },
                    { ref: "FDA", desc: "AI/ML-Enabled Medical Devices Database (1,451 cleared)." },
                    { ref: "MIT Sloan", desc: "Study on AI Initiative Success Rates (48% survival, 95% profitability gap)." },
                    { ref: "Crunchbase", desc: "Physical AI Scaleup Companies (254 identified, 2026)." },
                    { ref: "Cervicorn Consulting", desc: "Physical AI Market Report (2026)." },
                    { ref: "Carlota Perez", desc: "Technological Revolutions and Financial Capital (2002)." },
                    { ref: "Schwartz, S.H.", desc: "Theory of Basic Human Values (2012)." },
                  ].map((item, i) => (
                    <div key={i} className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-2.5">
                      <p className="text-xs font-semibold text-blue-400">{item.ref}</p>
                      <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─── SECTION 9: Built By ─── */}
      <section className="pb-8" data-testid="about-researcher">
        <Reveal>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6 md:p-8">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] mb-4">Built by</p>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-full bg-violet-500/15">
                <User className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Priyanka Jha</h2>
                <a
                  href="https://www.linkedin.com/in/priyankajh"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  data-testid="linkedin-link"
                >
                  <Linkedin className="w-3.5 h-3.5" />
                  LinkedIn
                </a>
              </div>
            </div>
            <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>
                Priyanka is a researcher and systems thinker working at the intersection of innovation, artificial intelligence, and societal progress. Her work centers on <span className="text-white/90 font-medium">conscious innovation</span> — the idea that technological evolution must align with human dignity, fairness, and collective well-being.
              </p>
              <p>
                She leads the <span className="text-emerald-400 font-medium">Amplifying Conscious Innovation</span> initiative and founded <span className="text-cyan-400 font-medium">IdeaFountain</span>, an AI-powered platform exploring originality and human-AI collaboration.
              </p>
              <p>
                Her research papers are available on SSRN: <a href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=6492398" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 font-medium transition-colors">"The Conscious Innovation Index (CII): A 7-Dimension Framework for Measuring the Ethical Alignment of Artificial Intelligence"</a> introduces the CII and presents a global analysis of {totalEntities} AI entities across {countriesCount} countries. Her second paper, <a href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=6481799" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 font-medium transition-colors">"The Possibility Engine: A Data-Grounded Framework for Software-Hardware AI Convergence and Future Capability Mapping"</a>, presents the methodology behind the Observatory's convergence analysis.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─── Footer ─── */}
      <footer className="text-center pt-8 pb-4 border-t border-white/[0.06]">
        <p className="text-xs text-muted-foreground">&copy; 2026 Priyanka Jha. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default AboutPage;
